import { useEffect, useRef, memo } from "react";
import DOMPurify from "dompurify";
import { getVisualAssetUrl, resolveQuestionHtmlAssetUrl } from "../../../api";

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "ul", "ol", "li", "img", "code", "pre", "sub", "sup", "span", "div", "h1", "h2", "h3", "h4", "h5", "h6", "a", "blockquote", "table", "tr", "td", "th", "thead", "tbody", "tfoot", "caption", "colgroup", "col"],
  ALLOWED_ATTR: ["src", "alt", "href", "class", "style", "colspan", "rowspan", "scope", "data-list", "target", "rel"],
};

// Neutralize *currency* dollar signs so MathJax's `$…$` inline-math scan never swallows them, while
// leaving genuine inline math ("$52.28 - 43.9$") for MathJax to typeset.
//
// The "$" delimiter is overloaded: "$72" / "$76.32" are prices (a lone unmatched "$" that MathJax
// otherwise renders as a stray delimiter), whereas "$52.28 - 43.9$" is a balanced math span. We tell
// them apart by content: a balanced "$…$" pair whose inner text carries a math operator or LaTeX
// command is real math and left untouched; every other "$<amount>" is a price and gets wrapped in a
// `tex2jax_ignore` span (MathJax 3's default ignore class) so it renders as plain text.
const MATH_SPAN_CONTENT_RE = /[-+/*^=<>×÷]|\\[a-zA-Z]/;
const wrapCurrencyAmounts = (seg) =>
  seg.replace(/\$\s?\d[\d.,]*/g, (amount) => `<span class="tex2jax_ignore">${amount}</span>`);
const protectCurrencyDollars = (text) => {
  if (!text || !text.includes("$")) return text;
  return text
    .split(/(\$\$[^$]*\$\$|\$[^$\n]+\$)/)
    .map((seg, i) => {
      if (i % 2 === 0) return wrapCurrencyAmounts(seg); // plain text between math spans
      // Candidate balanced "$…$" — keep as math only if the content looks like a real expression;
      // otherwise it was two adjacent prices (e.g. "$72 to $90"), so protect them instead.
      const inner = seg.replace(/^\$\$?|\$\$?$/g, "");
      return MATH_SPAN_CONTENT_RE.test(inner) ? seg : wrapCurrencyAmounts(seg);
    })
    .join("");
};

// MathJax typesetting is expensive and runs per-instance. Skip it entirely when the rendered HTML
// has no math delimiters — true for the bulk of MCQ options, short answers, and plain-text stems.
// Currency "$" ("$72", "$76.32") is NOT math: remove prices from the non-math segments first (same
// split as protectCurrencyDollars) so a lone price never triggers MathJax — which would render a
// stray "$" if its tex2jax_ignore config is unavailable. Genuine "$…$" math still triggers it.
const containsMath = (s) => {
  if (!s) return false;
  if (s.includes("\\(") || s.includes("\\[")) return true;
  // Remove currency amounts ("$72", "$76.32"); if any "$" delimiter still remains it's real math.
  // Conservative: mixed price+math text (e.g. "$5 … $x^2$") keeps a "$" and still runs MathJax —
  // only text whose every "$" is a price skips it, so a lone price never emits a stray delimiter.
  const stripped = s.replace(/\$\s?\d[\d.,]*/g, "");
  return /\$/.test(stripped);
};

// Detect Quill rich-HTML output: has headings, images, lists, blockquotes, or multiple <p> blocks.
// When true, skip markdown-specific transformations that would corrupt or add noise to already-valid HTML.
const isRichHtml = (text) => {
  if (!text) return false;
  return /<(?:h[1-6]|img|ul|ol|blockquote)\b/i.test(text) ||
    (text.match(/<p[\s>]/gi) || []).length > 1;
};

// Convert \textcircled{N} or \textcircledN (LaTeX oval notation) to styled HTML spans.
// Used by exam papers that show circled numbers like 27+30+…=80 inside blue ovals.
// MathJax does not support \textcircled so we pre-process it to pure HTML.
const convertTextCircled = (text) => {
  if (!text || !text.includes("\\textcircled")) return text;
  let result = text.replace(
    /\\textcircled\{([^}]*)\}|\\textcircled(\d[\d.]*|\.+)/g,
    (_, braced, bare) => {
      let content = braced !== undefined ? braced : bare;
      // Convert LaTeX dot commands (\dots, \ldots, \cdots, \dotso, etc.) to visual dots.
      // These appear in the blank oval (e.g. \textcircled{\dots\dots}) to indicate a missing value.
      content = content.replace(/\\(?:ldots|cdots|dotso|dotsc|dotsb|dotsi|dots)/g, "......");
      return (
        `<span style="display:inline-flex;align-items:center;justify-content:center;` +
        `background:#5b9bd5;color:#fff;border-radius:50px;padding:3px 10px;` +
        `font-weight:700;font-size:0.9em;min-width:32px;` +
        `line-height:1.4;margin:0 2px;vertical-align:middle">${content}</span>`
      );
    }
  );
  // Strip orphaned $ delimiters that used to wrap \textcircled expressions.
  // After conversion, "$ <span>27</span> + ... $" is left — the $ are no longer valid LaTeX
  // and would render as visible text. Remove them when they surround converted oval spans.
  result = result.replace(/\$\s*(<span\s[^>]*background:#5b9bd5)/g, '$1');
  result = result.replace(/(<\/span>)\s*\$/g, '$1');
  return result;
};

// Strip leading question numbers like "Q1)" or "1." from question_text.
// QuestionPaperLayout already renders "Q1)" via the questionNumber prop,
// so if the AI also includes it in the text, it appears twice.
// IMPORTANT: requires at least one punctuation character after the number (")"/"."/":"),
// so "8 pencils…" (digit + space only) is never stripped.
const stripLeadingQNumber = (text) => {
  if (!text) return text;
  // Require at least one space/tab after the punctuation so decimal numbers like
  // "0.09", "1.973", "0.013" are never mistaken for "question 0) 9", "1) 973" etc.
  return text.replace(/^\s*Q?\d{1,3}[).:][ \t]+/i, "").trimStart();
};

// Fix plain dots (.........) inside $...$ math delimiters.
// MathJax cannot render raw periods as dots — they must be \ldots.
// Without this fix, MathJax fails and leaves the $ visible as text.
const fixDotsInMath = (text) => {
  if (!text || !text.includes("$")) return text;
  // Match $$...$$ (display math) before $...$ (inline math) so the trailing $
  // of a display block is never treated as the opening of a new inline match
  // that spans across plain-text answer blanks (.........) between blocks.
  return text.replace(/\$\$([^$]*)\$\$|\$([^$]+)\$/g, (match, display, inline) => {
    if (display !== undefined) {
      return `$$${display.replace(/\.{3,}/g, "\\ldots")}$$`;
    }
    return `$${inline.replace(/\.{3,}/g, "\\ldots")}$`;
  });
};

// Convert Unicode superscript digits (²³¹⁰⁴⁵⁶⁷⁸⁹) into <sup> so "83²" / "cm²" render
// as a proper raised power instead of a baseline "832" / "cm2" the eye misreads. Only a
// run of superscripts directly following a letter, digit, or ")" is treated as an
// exponent, so surrounding text and math stay untouched.
const SUPERSCRIPT_MAP = { "⁰": "0", "¹": "1", "²": "2", "³": "3", "⁴": "4", "⁵": "5", "⁶": "6", "⁷": "7", "⁸": "8", "⁹": "9" };
const convertUnicodeSuperscripts = (text) => {
  if (!text || !/[⁰¹²³⁴-⁹]/.test(text)) return text;
  return text.replace(/([A-Za-z\d)])([⁰¹²³⁴-⁹]+)/g, (_, base, sup) => {
    const digits = sup.split("").map((ch) => SUPERSCRIPT_MAP[ch] ?? "").join("");
    return `${base}<sup>${digits}</sup>`;
  });
};

// Normalize degree symbol: replace °\circ (duplicate) or bare \circ with °.
// The PDF extractor stores the Unicode ° directly; the AI sometimes also appends \circ,
// resulting in "307°\circ" or "21°\circC". Strip the redundant LaTeX form.
const normalizeCirc = (text) => {
  if (!text || !text.includes("\\circ")) return text;
  // Covers: ^\circ  ^{\circ}  °\circ  bare \circ  → all become °
  return text.replace(/\^(?:\{)?\\circ(?:\})?|°?\\circ/g, "°");
};

// Fix malformed $ delimiters common in hub-spoke blank_labels and hub regex captures:
// - "= \\frac{1}{2}$" captures "\\frac{1}{2}$" with a closing $ but no opening $ — autoWrap
//   skips because "$" is present, leaving MathJax unable to render.
// - Fragments like "$\\frac{1}{16} + " after splitting on □ have an odd $ count.
const normalizeDanglingMathDelimiters = (text) => {
  if (!text || (!text.includes("$") && !/\\[a-zA-Z]/.test(text))) return text;
  let t = text;
  const trimmed = t.trim();
  // Leading backslash + LaTeX command, ends with $, but missing opening $ (whole-string hub capture)
  if (/^\\[a-zA-Z]/.test(trimmed) && trimmed.endsWith("$") && !trimmed.startsWith("$")) {
    t = `$${trimmed}`;
  }
  const dollarCount = (t.match(/\$/g) || []).length;
  if (dollarCount % 2 === 1) {
    t = `${t}$`;
  }
  return t;
};

// Auto-wrap bare LaTeX math commands in $...$ so MathJax can render them.
// The AI sometimes stores text like "Kapil eats \frac{3}{20} of the pizza" or a
// pure-math answer "\frac{3}{8} or \frac{6}{16}" without any $ delimiters.
// Each recognised command is wrapped individually so surrounding plain text is preserved.

// Quick-detect regex — cheap pre-check before running the full replacement.
const LATEX_MATH_CMD_RE = /\\(?:frac|sqrt|times|div|pm|mp|cdot|leq?|geq?|neq|approx|equiv|infty|oplus|otimes|ominus|oslash|cup|cap|setminus|emptyset|in|notin|subset|supset|subseteq|supseteq|angle|triangle|perp|parallel|therefore|because|rightarrow|leftarrow|Rightarrow|Leftarrow|leftrightarrow|Leftrightarrow|to|mapsto|implies|iff|neg|land|lor|cot|sec|csc|arcsin|arccos|arctan|pmod|bmod|pi|alpha|beta|gamma|delta|epsilon|varepsilon|zeta|eta|theta|iota|kappa|lambda|mu|nu|xi|rho|sigma|tau|upsilon|phi|chi|psi|omega|Gamma|Delta|Lambda|Xi|Pi|Sigma|Upsilon|Phi|Psi|Omega|sum|prod|int|lim|log|ln|sin|cos|tan|circ|ast|bullet|nabla|partial|Re|Im|text|operatorname|overline|underline|hat|bar|vec|tilde|mathbf|mathrm|mathit|mathbb|mathcal|boldsymbol|dots|ldots|cdots|dotso|dotsc|dotsb|dotsi|vdots|ddots|quad|qquad)\b/;

// Replacement regex — matches one complete "math atom":
//   • Optional leading integer/decimal directly adjacent (e.g. the "72" in "72\text{cm}^2")
//   • A recognised LaTeX command
//   • Zero or more {brace} argument groups (one level of nesting for \frac{a/b}{c/d})
//   • Zero or more trailing ^{...} / _{...} / ^n / _n superscripts or subscripts
const BARE_LATEX_RE = /(?:\d+(?:\.\d+)?)?\\(?:frac|sqrt|times|div|pm|mp|cdot|leq?|geq?|neq|approx|equiv|infty|oplus|otimes|ominus|oslash|cup|cap|setminus|emptyset|in|notin|subset|supset|subseteq|supseteq|angle|triangle|perp|parallel|therefore|because|rightarrow|leftarrow|Rightarrow|Leftarrow|leftrightarrow|Leftrightarrow|to|mapsto|implies|iff|neg|land|lor|cot|sec|csc|arcsin|arccos|arctan|pmod|bmod|pi|alpha|beta|gamma|delta|epsilon|varepsilon|zeta|eta|theta|iota|kappa|lambda|mu|nu|xi|rho|sigma|tau|upsilon|phi|chi|psi|omega|Gamma|Delta|Lambda|Xi|Pi|Sigma|Upsilon|Phi|Psi|Omega|sum|prod|int|lim|log|ln|sin|cos|tan|circ|ast|bullet|nabla|partial|Re|Im|text|operatorname|overline|underline|hat|bar|vec|tilde|mathbf|mathrm|mathit|mathbb|mathcal|boldsymbol|dots|ldots|cdots|dotso|dotsc|dotsb|dotsi|vdots|ddots|quad|qquad)(?:\{(?:[^{}]|\{[^{}]*\})*\})*(?:[_^](?:\{[^{}]*\}|\S))*/g;

// Wrap bare commands in plain-text segments that fall outside existing $...$ blocks.
// Used when text already has some delimited math — autoWrapLatexCommands skips such
// text entirely, leaving bare commands like \oplus or \ldots un-typeset.
const wrapBareCommandsOutsideMath = (text) => {
  if (!text || !LATEX_MATH_CMD_RE.test(text)) return text;
  // Split into alternating plain-text / math segments.
  // Match $$...$$ (display math) before $...$ (inline math) so the two-dollar
  // delimiter is never misread as two consecutive empty inline-math blocks.
  const segments = text.split(/(\$\$[^$]*\$\$|\$[^$]*\$)/);
  return segments.map((seg, i) => {
    if (i % 2 === 1) return seg; // inside math — leave as-is
    return seg.replace(BARE_LATEX_RE, (match) => `$${match}$`);
  }).join('');
};

// Wrap bare caret exponents (5^2, x^3, n^{10}) in $...$ so MathJax renders them as real
// powers instead of literal "5^2". Only touches plain-text outside existing $...$ / \(...\)
// math so already-delimited exponents are left for MathJax as-is.
const wrapBareExponents = (text) => {
  if (!text || !text.includes("^")) return text;
  const wrap = (seg) =>
    seg.replace(/([A-Za-z\d]+)\^(\{[^{}]*\}|[A-Za-z\d]+)/g, (_, base, exp) => `$${base}^${exp}$`);
  if (text.includes("\\(") || text.includes("\\[")) return text;
  if (!text.includes("$")) return wrap(text);
  return text
    .split(/(\$\$[^$]*\$\$|\$[^$]*\$)/)
    .map((seg, i) => (i % 2 === 1 ? seg : wrap(seg)))
    .join("");
};

const autoWrapLatexCommands = (text) => {
  if (!text || !text.includes("\\")) return text;
  // Text already uses \(...\) or \[...\] delimiters — skip to avoid nesting.
  if (text.includes("\\(") || text.includes("\\[")) return text;
  if (text.includes("$")) {
    // Text has some delimited math — wrap bare commands only in plain-text segments.
    return wrapBareCommandsOutsideMath(text);
  }
  if (!LATEX_MATH_CMD_RE.test(text)) return text;
  return text.replace(BARE_LATEX_RE, (match) => `$${match}$`);
};

// Strip MCQ option labels (A) text B) text... or A. text B. text...) from question text.
// The AI sometimes embeds options in question_text even though they're stored separately
// in the options array. Paper shows only the question stem, not inline options.
// Requires 3+ consecutive lettered options to avoid false positives.
// Also handles GL VR formats: "**A** m  **B** e  **C** t" (bold labels) and
// plain-space "A m  B e  C t" tail rows.
const stripInlineMCQOptions = (text) => {
  if (!text) return text;
  let working = text;

  // Strip fill-in-blank underscores from table cells, keeping the | pipe intact
  working = working.replace(/([|])\s*_{2,}\s*/g, '$1 ');

  // GL VR bold-labeled option row: **A** x  **B** y  **C** z ...
  // (at least 3 bold letters on a trailing line)
  const glBoldRe = /\n+(?:\*\*[A-EX-Z]\*\*\s+\S+(?:\s{2,}|\s*$)){3,}\s*$/;
  if (glBoldRe.test(working)) {
    working = working.replace(glBoldRe, '').trim();
  }

  // Plain space-separated trailing option row: "A x  B y  C z  D w  E v"
  // (3+ single-letter labels followed by short token, each separated by 2+ spaces or EOL)
  const plainTailRe = /\n+(?:[A-EX-Z]\s+\S{1,15}(?:\s{2,}|\s*$)){3,}\s*$/;
  if (plainTailRe.test(working)) {
    working = working.replace(plainTailRe, '').trim();
  }

  // Legacy "A) text B) text..." format — require 2+ spaces or newline before "A." so
  // mid-sentence occurrences like "bag A. There" (single space) are never mistaken for option labels.
  const optStart = working.search(/(?:\s{2,}|[\n\r])A[).]\s+\S/);
  if (optStart >= 10) {
    const afterA = working.substring(optStart);
    const labelCount = (afterA.match(/(?:\s{2,}|[\n\r])[A-E][).]\s+/g) || []).length;
    if (labelCount >= 3) {
      working = working.substring(0, optStart).trim();
    }
  }

  return working.trim();
};

// Detect bullet-list items that the AI compressed onto a single line.
// Pattern: "Some stem text, - Item one - Then item two - And item three suffix"
// When 2+ occurrences of " - CapitalWord" appear in one line we split at each boundary,
// converting to separate "- item" lines that convertMarkdownLists can then render.
const normalizeInlineBulletLists = (text) => {
  return text
    .split("\n")
    .map((line) => {
      // Need at least 2 occurrences of " - [Capital][lower]" to be an inline list
      const bulletMatches = line.match(/ - [A-Z][a-z]/g);
      if (!bulletMatches || bulletMatches.length < 2) return line;

      // Split at each " - CapitalLower" boundary (space before dash)
      const splitParts = line.split(/ (?=- [A-Z][a-z])/);
      if (splitParts.length < 3) return line; // stem + at least 2 list items

      const stem = splitParts[0];
      const listItems = splitParts.slice(1);

      // Last list item may have non-list continuation text after a sentence boundary
      // e.g. "- And then Squares it and gets 121 Circle the number that Nisha was thinking of."
      // We keep it all in the last bullet item and let the suffix (MCQ choices etc.) fall on own line.
      const lastItem = listItems[listItems.length - 1];
      const suffixMatch = lastItem.match(/^(- [^.!?]*[.!?])\s+(\S[^-].*)/);
      let cleanItems = listItems;
      let suffix = "";
      if (suffixMatch) {
        cleanItems = [...listItems.slice(0, -1), suffixMatch[1]];
        suffix = suffixMatch[2];
      }

      // Fallback: when the last bullet doesn't end with .!? (e.g. "£ 3.00 each")
      // the punctuation-based regex fails. If the last item is suspiciously long
      // compared to the first bullet, try splitting at a sentence-start pattern.
      if (!suffixMatch) {
        const firstItemLen = cleanItems[0]?.length || 20;
        if (lastItem.length > firstItemLen * 2) {
          const altMatch = lastItem.match(/^(- \S.{5,}?)\s+([A-Z][a-z]+ [a-z].*)/);
          if (altMatch && altMatch[2].length > 15) {
            cleanItems = [...listItems.slice(0, -1), altMatch[1]];
            suffix = altMatch[2];
          }
        }
      }

      const result = [];
      if (stem.trim()) result.push(stem.trim());
      cleanItems.forEach((item) => result.push(item.trim()));
      if (suffix.trim()) result.push(suffix.trim());
      return result.join("\n");
    })
    .join("\n");
};

// Convert markdown unordered-list lines (- item / * item / • item) to <ul><li>.
// Runs after table conversion so table-row pipes are not mistaken for list content.
// Preserves consecutive list blocks; non-list lines pass through unchanged.
const convertMarkdownLists = (text) => {
  const lines = text.split("\n");
  const out = [];
  let inList = false;

  for (const line of lines) {
    const m = line.match(/^([-*•])\s+(.+)$/);
    if (m) {
      if (!inList) {
        out.push('<ul style="margin:6px 0 6px 22px;padding:0;line-height:1.7">');
        inList = true;
      }
      out.push(`<li style="margin-bottom:3px">${m[2]}</li>`);
    } else {
      if (inList) {
        out.push("</ul>");
        inList = false;
      }
      out.push(line);
    }
  }
  if (inList) out.push("</ul>");

  return out.join("\n");
};

const stripStepNumber = (s) => {
  let result = String(s || "").trim();
  let prev = "";
  while (prev !== result) {
    prev = result;
    result = result.replace(/^\d+[.)]\s+/, "").trim();
  }
  return result;
};

const NUMBERED_STEP_RE = /^\d+[.)]\s+/;
const OL_STYLE = 'margin:6px 0 6px 22px;padding:0;line-height:1.7';
const LI_STYLE = 'margin-bottom:4px';

// A genuine numbered-steps list counts 1, 2, 3, … in order — an incidental "digit + '.'/')' + space"
// inside ordinary prose or a maths expression (a sentence ending "...about 6. What next?", or a
// bracketed calculation "(31 × 9) ÷ 52.") does not, and its numbers are never sequential from 1.
// Without this check those false matches got wrapped in an <ol>, whose CSS auto-numbering (1, 2, 3…)
// silently replaced the real digits and made "9) ÷ 52." disappear into list markup.
const stepsAreSequential = (candidateLines) => {
  const nums = candidateLines.map((l) => parseInt(l.trim(), 10));
  return nums.length >= 2 && nums.every((n, i) => n === i + 1);
};

// Convert numbered worked steps (1. / 1) / Step 1:) into <ol><li>, including
// single-line AI explanations like "Method. 1. First 2. Second So the answer is A."
const convertMarkdownNumberedLists = (text) => {
  if (!text || /<ol[\s>]/i.test(text)) return text;

  const lines = text.split("\n");
  const numberedLines = lines.filter((l) => l.trim() && NUMBERED_STEP_RE.test(l.trim()));

  if (numberedLines.length >= 2 && stepsAreSequential(numberedLines)) {
    const out = [];
    let inList = false;
    for (const line of lines) {
      const trimmed = line.trim();
      if (NUMBERED_STEP_RE.test(trimmed)) {
        if (!inList) {
          out.push(`<ol style="${OL_STYLE}">`);
          inList = true;
        }
        out.push(`<li style="${LI_STYLE}">${stripStepNumber(trimmed)}</li>`);
      } else {
        if (inList) {
          out.push("</ol>");
          inList = false;
        }
        if (trimmed) out.push(line);
      }
    }
    if (inList) out.push("</ol>");
    return out.join("\n");
  }

  const single = lines.map((l) => l.trim()).filter(Boolean).join(" ");
  const parts = single.split(/(?=\d+[.)]\s)/).filter((p) => p.trim());
  const numberedParts = parts.filter((p) => NUMBERED_STEP_RE.test(p.trim()));
  if (numberedParts.length >= 2 && stepsAreSequential(numberedParts)) {
    const prefix = parts[0] && !NUMBERED_STEP_RE.test(parts[0].trim())
      ? `<p style="margin:0 0 6px">${parts[0].trim()}</p>`
      : "";
    const items = numberedParts
      .map((p) => `<li style="${LI_STYLE}">${stripStepNumber(p.trim())}</li>`)
      .join("");
    return `${prefix}<ol style="${OL_STYLE}">${items}</ol>`;
  }

  return text;
};

// Convert remaining newlines (outside HTML table blocks) to <br> so that
// question text with sub-parts like "a) …\nb) …" or spaced digit rows
// display as separate visual lines.  Table HTML produced by convertMarkdownTables
// is single-line per block so <br> around it is harmless.
// PDF extraction often wraps long words at the page margin, leaving a bare \n
// between two word characters (e.g. "anti\nclockwise", "po\nint").  Replace those
// with a space before the general \n→<br> pass so the word rejoins naturally.
const convertNewlinesToBr = (text) =>
  text
    .replace(/([a-zA-Z0-9])\n([a-z])/g, "$1$2")
    .replace(/\n/g, "<br>");

// Deduplicate question text — AI sometimes outputs the question body twice,
// either in separate paragraphs or inline without a newline separator.
// NOTE: This function skips markdown table content (lines with | and --- separators)
// to avoid incorrectly detecting table syntax as duplicate text.
const dedupeText = (text) => {
  if (!text || text.length < 20) return text;
  
  // Check if text contains a markdown table - if so, check for duplicates more carefully
  // because the AI might be outputting the table+question twice
  const hasMarkdownTable = /[|][^\n]+[|]/.test(text) && /[|][\s\-:]+[|]/.test(text);
  
  if (hasMarkdownTable) {
    // For table questions, split by double newlines and check for duplicates
    // This catches cases where AI outputs: "table\n\nquestion\n\ntable\n\nquestion"
    const paras = text.split(/\n\n+/).map((s) => s.trim()).filter(Boolean);
    if (paras.length >= 2) {
      // Check if all paragraphs are identical (full duplication)
      const firstPara = paras[0];
      const allSame = paras.every(p => p === firstPara);
      if (allSame) {
        return firstPara;
      }
      // Check if first half equals second half (the table+question repeated)
      const mid = Math.floor(paras.length / 2);
      const firstHalf = paras.slice(0, mid).join('\n\n');
      const secondHalf = paras.slice(mid).join('\n\n');
      if (firstHalf === secondHalf && firstHalf.length > 20) {
        return firstHalf;
      }
    }
    // If no clear duplication pattern found, return as-is to preserve table+question
    return text;
  }
  
  const toPlain = (s) => s.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  // Require space/tab after punctuation so "0.09" is not stripped to "09"
  const stripQNum = (s) => s.replace(/^Q?\d+[).:]\s+/i, "").trim();
  const normalizeBody = (s) => stripQNum(toPlain(s)).toLowerCase();

  // Case 1: paragraph-separated duplicates (split by \n\n)
  const paras = text.split(/\n\n+/).map((s) => s.trim()).filter(Boolean);
  if (paras.length >= 2) {
    const norm0 = normalizeBody(paras[0]);
    if (paras.slice(1).some((p) => normalizeBody(p) === norm0)) {
      return paras[0];
    }
  }

  // Case 2: inline duplicate — plain body is "X X" (text repeated without newline).
  // Guard on the STRIPPED body length, not the raw text length: a short numeric/word
  // option like "11" or "1212" wrapped in markup (<p><strong>11</strong></p>) clears the
  // 20-char raw-length guard above, but its plain body ("11") is just a digit repeated —
  // the halves-equal check would wrongly collapse it to "1". Real duplicated question text
  // is far longer, so only run this detection once the body itself is substantial.
  const fullNorm = normalizeBody(text);
  const len = fullNorm.length;
  if (len < 20) return text;
  for (let i = Math.floor(len * 0.3); i <= Math.floor(len * 0.65); i++) {
    if (fullNorm.substring(0, i).trim() === fullNorm.substring(i).trim()) {
      const qMatch = text.match(/^(Q?\d+[).:\s]*)/i);
      const qPrefix = qMatch ? qMatch[1] : "";
      const rawBody = text.slice(qPrefix.length);
      let rIdx = 0, nCount = 0;
      while (rIdx < rawBody.length && nCount < i) {
        if (rawBody[rIdx] === "<") {
          const end = rawBody.indexOf(">", rIdx);
          rIdx = (end > rIdx ? end : rIdx) + 1;
        } else { nCount++; rIdx++; }
      }
      const chunk = rawBody.substring(0, rIdx);
      const cutAt = Math.max(chunk.lastIndexOf("?"), chunk.lastIndexOf("."));
      if (cutAt > rIdx * 0.4) return (qPrefix + rawBody.substring(0, cutAt + 1)).trim();
      return (qPrefix + chunk).trim();
    }
  }
  return text;
};

// Detect "digit/value rows" that the AI collapsed onto one line.
// Example: "Look at these digits 7 9 0 3 8 4 Make the largest number possible..."
// The paper shows the digit sequence on its own prominent line between the intro sentence and the question.
// Heuristic: when a line contains a sequence of 3+ single-digit numbers (or short values) separated
// only by spaces between two sentence fragments, insert \n before and after that sequence.
const normalizeDigitRows = (text) => {
  // Pattern: sentence-ending text, then 3+ space-separated digits/short-values, then more sentence text
  // e.g. "Look at these digits 7 9 0 3 8 4 Make the largest..."
  return text.replace(
    /([A-Za-z,.:]\s+)((?:\d{1,5}\s+){2,}\d{1,5})(\s+[A-Z])/g,
    (_, before, digits, after) => `${before.trimEnd()}\n**${digits.trim()}**\n${after.trimStart()}`
  );
};

// Try to expand a compact single-line table where all rows are concatenated with no spaces.
// Example: "|||||---|----|---|||3|||6|?|6|7||||9||"
// Strategy:
//   1. Split into pipe-delimited tokens.
//   2. Locate the separator block (consecutive all-dash/colon tokens, ≥2 cells).
//   3. Reconstruct header and data rows using the separator as an anchor.
//   4. Return multiline markdown that convertMarkdownTables can process.
// Returns null when the line doesn't match the compact-table pattern.
const tryExpandCompactTable = (line) => {
  if (!line.includes("|") || !/[|][-:]{2,}[|]/.test(line)) return null;

  const tokens = line.split("|");
  // Locate separator block: run of consecutive all-dash/colon tokens
  let sepStart = -1;
  let sepEnd = -1;
  for (let k = 1; k < tokens.length - 1; k++) {
    const t = tokens[k].trim();
    if (t.length >= 2 && /^[-:]+$/.test(t)) {
      if (sepStart === -1) sepStart = k;
      sepEnd = k;
    } else if (sepStart !== -1) {
      break; // end of consecutive separator block
    }
  }
  if (sepStart === -1 || sepEnd - sepStart < 1) return null; // need ≥2 separator cells

  const sepCells = tokens.slice(sepStart, sepEnd + 1);
  const N_sep = sepCells.length;

  // ── Parse header row(s): tokens before the separator ────────────────────────
  // In the compact format the header row immediately precedes the separator with
  // a shared boundary pipe. The header's inner cells are tokens[1 .. sepStart-2].
  const headerTokens = tokens.slice(1, Math.max(1, sepStart - 1));
  const headerCells = headerTokens.map((t) => t.trim());

  // ── Parse data rows: tokens after the separator ──────────────────────────────
  // After the separator's closing boundary (tokens[sepEnd+1]) come the data rows.
  // We identify row content by finding "data groups" — runs of consecutive
  // non-empty tokens — and use the surrounding empty tokens to count leading
  // blank cells (column offset within the row).
  const postTokens = tokens.slice(sepEnd + 1);

  // Find all data groups (consecutive non-empty token runs) and their leading
  // empty-token counts (which tell us how many blank cells precede the group).
  const groups = [];
  let runStart = -1;
  let leadingEmpties = 0;
  let inRun = false;

  for (let k = 0; k < postTokens.length; k++) {
    const isEmpty = postTokens[k].trim() === "";
    if (!isEmpty) {
      if (!inRun) {
        runStart = k;
        inRun = true;
        // Count consecutive empties immediately before this run
        let e = 0;
        let j = k - 1;
        while (j >= 0 && postTokens[j].trim() === "") { e++; j--; }
        leadingEmpties = e;
      }
    } else {
      if (inRun) {
        groups.push({ cells: postTokens.slice(runStart, k).map((t) => t.trim()), leadingEmpties });
        inRun = false;
      }
    }
  }
  if (inRun) {
    groups.push({ cells: postTokens.slice(runStart).map((t) => t.trim()), leadingEmpties });
  }

  if (groups.length === 0) return null;

  // Effective column count = max of separator count and longest data group
  const maxGroupLen = Math.max(...groups.map((g) => g.cells.length));
  const N = Math.max(N_sep, maxGroupLen);

  // Build each data row: place the group cells at the inferred column offset,
  // then pad to N columns.
  // Leading empties: the first empty after the separator boundary is a section
  // opener (not a cell), so we subtract 1 to get the actual cell offset.
  const dataRows = groups.map((g) => {
    const offset = Math.max(0, g.leadingEmpties - 1);
    const row = Array(N).fill("");
    g.cells.forEach((c, j) => {
      if (offset + j < N) row[offset + j] = c;
    });
    return row;
  });

  // Pad header to N columns
  const paddedHeader = [...headerCells];
  while (paddedHeader.length < N) paddedHeader.push("");

  const headerLine = "|" + paddedHeader.join("|") + "|";
  const sepLine    = "|" + Array(N).fill("---").join("|") + "|";
  const dataLines  = dataRows.map((r) => "|" + r.join("|") + "|");

  return [headerLine, sepLine, ...dataLines].join("\n");
};

// Detect markdown tables that have been "flattened" onto a single line (no newlines between rows).
// This happens when the AI outputs JSON with table rows collapsed into one line.
// Strategy: find row boundaries using `| |` (pipe → whitespace → pipe) which ONLY occurs between
// rows — not inside rows where cells are `| content |` (next char after spaces is content, not `|`).
// Falls back to tryExpandCompactTable for tables with no whitespace between pipes.
// Also handles separator-less inline tables where | | acts as a row boundary (e.g. shop comparison
// tables: "prose | Shop A | | Chair – £600 | | Special Offer |" with no |---|---| row).
const normalizeInlineTables = (text) => {
  if (!text.includes("|")) return text;
  let inMarkdownTable = false;
  return text
    .split("\n")
    .map((line) => {
      if (!line.includes("|")) {
        inMarkdownTable = false;
        return line;
      }

      // Separator row — entering a proper markdown table block
      if (/^\s*[|][ \t]*:?-+:?[ \t]*[|]/.test(line.trim())) {
        inMarkdownTable = true;
        return line;
      }

      // Inside a markdown table data row — skip inline normalization to avoid
      // splitting empty cells (| |) as if they were row boundaries
      if (inMarkdownTable && /^\s*[|]/.test(line)) {
        return line;
      }

      if (!/^\s*[|]/.test(line)) {
        inMarkdownTable = false;
      }

      const hasInlineSep = /[|]\s*-{1,}[-:\s|]*[|]/.test(line);
      // For separator-less multi-row tables: count how many | | (pipe+whitespace+pipe) row boundaries
      // exist. Require ≥2 to avoid false positives (a single | | can appear in normal prose or a
      // 3-cell table where the middle cell is empty).
      const rowBoundaryCount = hasInlineSep ? Infinity : (line.match(/\|\s+\|/g) || []).length;

      // Quick exit: no separator AND not enough row-boundary evidence
      if (!hasInlineSep && rowBoundaryCount < 2) return line;

      // Split at row boundaries: pipe + whitespace + lookahead-pipe
      // This matches only at row boundaries because within a row the character after spaces
      // is cell content, not another `|`.
      const segments = line.split(/[|]\s+(?=[|])/);
      if (segments.length > 1) {
        // Rebuild rows: add back the trailing | that was consumed by the split
        const rows = segments.map((seg, i) =>
          i < segments.length - 1 ? seg + "|" : seg
        );

        // Must contain at least one separator row (cells of dashes/colons only) OR
        // have enough double-pipe row boundaries to be confident this is a real table.
        const hasSeparator = rows.some((r) => /^[|][ \t]*:?-+:?[ \t]*[|]/.test(r.trim()));
        if (!hasSeparator && rowBoundaryCount < 2) return line;

        // First segment may contain prefix text before the first `|`
        const firstPipeIdx = rows[0].indexOf("|");
        const prefix =
          firstPipeIdx > 0 ? rows[0].substring(0, firstPipeIdx).trim() : "";
        const cleanFirst =
          firstPipeIdx > 0 ? rows[0].substring(firstPipeIdx) : rows[0];

        // Last segment may contain suffix text after the final `|`
        const lastRow = rows[rows.length - 1];
        const lastPipeIdx = lastRow.lastIndexOf("|");
        let suffix = "";
        let cleanLast = lastRow;
        if (lastPipeIdx >= 0 && lastPipeIdx < lastRow.length - 1) {
          const afterLastPipe = lastRow.substring(lastPipeIdx + 1).trim();
          if (afterLastPipe && !afterLastPipe.startsWith("|")) {
            suffix = afterLastPipe;
            cleanLast = lastRow.substring(0, lastPipeIdx + 1);
          }
        }

        // Reconstruct: prefix (if any) → table rows (newline-separated) → suffix (if any)
        const tableRows =
          rows.length === 1
            ? [cleanFirst]
            : [cleanFirst, ...rows.slice(1, -1), cleanLast];
        const parts = [];
        if (prefix) parts.push(prefix);
        parts.push(...tableRows);
        if (suffix) parts.push(suffix);
        return parts.join("\n");
      }

      // No spaces between pipes — try compact single-line table format
      // (e.g. "|||||---|----|---|||3|||6|?|6|7||||9||" from PDF extraction)
      if (hasInlineSep) {
        const expanded = tryExpandCompactTable(line);
        if (expanded) return expanded;
      }

      return line;
    })
    .join("\n");
};

// Merge "continuation" fragments back into their preceding table row.
// The PDF extractor sometimes wraps a wide table row across two physical lines:
//   | December | January | February | March | April | May |
//   June |          ← continuation: no leading |, only one trailing |
// These fragments must be merged before convertMarkdownTables runs, otherwise
// collectDataRows stops after 1 row (line doesn't start with |) and Case 2
// (needs 2+ rows) falls through — the table renders as plain text with raw pipes.
const joinSplitTableRows = (text) => {
  if (!text.includes("|")) return text;
  const lines = text.split("\n");
  const out = [];
  for (const line of lines) {
    const trimmed = line.trim();
    const pipeCount = (trimmed.match(/[|]/g) || []).length;
    if (out.length > 0) {
      const prevLine = out[out.length - 1];
      const prevPipeCount = (prevLine.match(/[|]/g) || []).length;
      // Merge continuation fragment: doesn't start with |, ends with |, and has
      // fewer pipes than the preceding full row (handles "60 |" and "5 | |" etc.)
      if (
        /^\s*[|]/.test(prevLine) &&
        !/^\s*[|]/.test(trimmed) &&
        /[|]\s*$/.test(trimmed) &&
        pipeCount >= 1 && pipeCount < prevPipeCount
      ) {
        out[out.length - 1] = prevLine.trimEnd() + " " + trimmed;
        continue;
      }
    }
    out.push(line);
  }
  return out.join("\n");
};

// Parse alignment from separator cell (e.g. :--- left, :---: center, ---: right)
const getAlign = (cell) => {
  const t = cell.trim();
  if (/^:[-:]+:$/.test(t)) return "center";
  if (/^:[-:]+$/.test(t)) return "left";
  if (/^[-:]+:$/.test(t)) return "right";
  return "left";
};

// Check if a row looks like a data row (numbers, single letters A–D, etc.) rather than a header.
// Used to render uniform cells for number grids like "89|90|91" / "94|A|B" / "C|96|D" to match paper.
const isDataGridRow = (cells) =>
  cells.length >= 2 &&
  cells.every(
    (c) =>
      /^\d+$/.test(c) ||
      /^[A-D]$/i.test(c) ||
      /^[A-Z]$/i.test(c) ||
      c === "" ||
      /^[\d.\-+]+$/.test(c)
  );

// Convert markdown table syntax to an HTML <table>.
// Handles: (1) header row | separator row (|---|---|) | data rows.
// (2) Tables WITHOUT separator: 2+ consecutive pipe-delimited lines → uniform grid (matches paper).
// Styled to resemble exam paper tables: clear borders, readable alignment, responsive.
const convertMarkdownTables = (text) => {
  const out = [];
  let i = 0;
  const parseRow = (row) =>
    row.split("|").filter((_, j, arr) => j > 0 && j < arr.length - 1).map((c) => c.trim());
  const baseTableStyle = "border-collapse:collapse;margin:12px 0;font-size:0.95rem;width:auto;max-width:100%;font-family:inherit";
  const cellStyle = (align) => `border:1px solid #333;padding:8px 12px;text-align:${align}`;
  const headerStyle = (align) => `border:1px solid #333;padding:8px 12px;background:#f0f0f0;text-align:${align};font-weight:600;white-space:nowrap`;

  // Extract trailing text from a table row that doesn't end with |
  // e.g. "| C | 96 | D | - A....." → row="| C | 96 | D |", trailing="- A....."
  const extractTrailing = (row) => {
    const trimmed = row.trimEnd();
    if (/[|]\s*$/.test(trimmed)) return { row: trimmed, trailing: "" };
    const lp = trimmed.lastIndexOf("|");
    if (lp === -1) return { row: trimmed, trailing: "" };
    return { row: trimmed.substring(0, lp + 1), trailing: trimmed.substring(lp + 1).trim() };
  };

  // Collect consecutive data rows (starting with |, not separator lines), extracting trailing text
  const collectDataRows = (startIdx) => {
    const dataRows = [];
    const trailingTexts = [];
    let j = startIdx;
    while (j < lines.length && /^\s*[|]/.test(lines[j]) && !/^\s*[|][ \t]*:?-+:?[ \t]*[|]/.test(lines[j].trim())) {
      const { row, trailing } = extractTrailing(lines[j]);
      dataRows.push(row);
      if (trailing) trailingTexts.push(trailing);
      j++;
    }
    return { dataRows, trailingTexts, endIdx: j };
  };

  const renderUniformTable = (rows) => {
    const allRows = rows.map((r) => parseRow(r));
    const maxCols = Math.max(...allRows.map((r) => r.length));
    const tbodyHtml = allRows
      .map((cells) => {
        const padded = [...cells];
        while (padded.length < maxCols) padded.push("");
        return `<tr>${padded.map((c, k) => `<td style="${cellStyle("left")}">${c}</td>`).join("")}</tr>`;
      })
      .join("");
    return `<div style="overflow-x:auto;margin:12px 0"><table style="${baseTableStyle}"><tbody>${tbodyHtml}</tbody></table></div>`;
  };

  // ── Pre-processing ────────────────────────────────────────────────────────
  // P1: Split inline table headers — prose ending with | col | col | ... |
  // e.g. "recorded. | Day | Mon | Tue |" → "recorded.\n| Day | Mon | Tue |"
  // Guards: prose must not start with |, table part must have ≥2 cells.
  const splitInlineHeaders = (raw) =>
    raw.replace(
      /^([^|\n][^\n]+?)(\s*[|](?:[^|\n]+[|]){2,})([ \t]*(?:<\/[a-zA-Z][^>]*>)*[ \t]*)$/gm,
      (_m, prose, tableRow, trailing) => {
        const tail = trailing.trim();
        return tail
          ? `${prose.trimEnd()}\n${tableRow.trim()}\n${tail}`
          : `${prose.trimEnd()}\n${tableRow.trim()}`;
      }
    );

  // P2: Collapse single blank lines between consecutive pipe-starting lines so
  // "| header |\n\n| data |" groups into one table instead of being treated separately.
  const collapseTableBlanks = (raw) => {
    let s = raw, prev;
    do { prev = s; s = s.replace(/^([ \t]*[|][^\n]+)\n[ \t]*\n([ \t]*[|])/gm, "$1\n$2"); }
    while (s !== prev);
    return s;
  };

  const preprocessed = collapseTableBlanks(splitInlineHeaders(text));
  const lines = preprocessed.split("\n");

  while (i < lines.length) {
    const line = lines[i];
    const next = i + 1 < lines.length ? lines[i + 1] : "";
    const nextTrimmed = next.trim();

    // Case 0: Separator-first headerless table — line IS a separator (|---|---), next is data row
    // e.g. "|----|----|\n| 89 | 90 |" → uniform grid with no header
    if (/^\s*[|][ \t]*:?-+:?[ \t]*[|]/.test(line.trim())) {
      const { dataRows, trailingTexts, endIdx } = collectDataRows(i + 1);
      if (dataRows.length > 0) {
        out.push(renderUniformTable(dataRows));
        trailingTexts.forEach((t) => out.push(t));
        i = endIdx;
        continue;
      }
      // No data rows after separator — treat as plain text
      out.push(line);
      i++;
      continue;
    }

    // Case 1: Standard markdown table — current line STARTS with |, next is separator (---, :--:, etc.)
    // Fixed: require line to start with | so plain text ending in |||| is not treated as a header row
    if (/^\s*[|]/.test(line) && /^\s*[|][ \t]*:?-+:?[ \t]*[|]/.test(nextTrimmed)) {
      const headers = parseRow(line);
      const sepCells = parseRow(nextTrimmed);
      i += 2;

      const { dataRows, trailingTexts, endIdx } = collectDataRows(i);
      i = endIdx;

      const parsedDataRows = dataRows.map((r) => parseRow(r));
      const maxCols = Math.max(headers.length, ...parsedDataRows.map((r) => r.length));
      const alignments = Array.from({ length: maxCols }, (_, idx) => getAlign(sepCells[idx] ?? ""));
      const paddedHeaders = [...headers];
      while (paddedHeaders.length < maxCols) paddedHeaders.push("");

      // Use uniform cell styling when header row looks like data (number grid, e.g. 89|90|91)
      const useUniformStyle = isDataGridRow(paddedHeaders);
      const thHtml = useUniformStyle
        ? paddedHeaders.map((c, j) => `<td style="${cellStyle(alignments[j] || "left")}">${c}</td>`).join("")
        : paddedHeaders.map((c, j) => `<th style="${headerStyle(alignments[j] || "left")}">${c}</th>`).join("");
      const tbodyHtml = parsedDataRows
        .map((cells) => {
          const paddedCells = [...cells];
          while (paddedCells.length < maxCols) paddedCells.push("");
          return `<tr>${paddedCells.map((c, j) => `<td style="${cellStyle(alignments[j] || "left")}">${c}</td>`).join("")}</tr>`;
        })
        .join("");
      const tableContent = useUniformStyle
        ? `<tbody><tr>${thHtml}</tr>${tbodyHtml}</tbody>`
        : `<thead><tr>${thHtml}</tr></thead><tbody>${tbodyHtml}</tbody>`;
      out.push(`<div style="overflow-x:auto;margin:12px 0"><table style="${baseTableStyle}">${tableContent}</table></div>`);
      trailingTexts.forEach((t) => out.push(t));
      continue;
    }

    // Case 2: Table WITHOUT separator — 2+ consecutive pipe-delimited lines starting with |
    // e.g. "| 89 | 90 | 91 |\n| 94 | A | B |\n| C | 96 | D |" → uniform 3x3 grid
    // Also handles a single isolated pipe-line with ≥4 cells (AI-collapsed data table row).
    if (/^\s*[|]/.test(line) && !/^\s*[|][\s\-:|]+[|]/.test(line.trim())) {
      const { dataRows, trailingTexts, endIdx } = collectDataRows(i);
      const isDataTable = dataRows.length >= 2 ||
        (dataRows.length === 1 && parseRow(dataRows[0]).length >= 4);
      if (isDataTable) {
        out.push(renderUniformTable(dataRows));
        trailingTexts.forEach((t) => out.push(t));
        i = endIdx;
        continue;
      }
    }

    out.push(line);
    i++;
  }
  return out.join("\n");
};

/**
 * Renders HTML/Markdown content with LaTeX math expressions.
 * - Deduplicates repeated question text (AI artifact)
 * - Converts markdown tables to HTML <table>
 * - Converts markdown images ![alt](url) to <img>
 * - Rewrites R2 / localhost proxy URLs to the correct environment URL
 * - Hides broken images (avoids showing raw alt-text when URL is invalid)
 * - Typesets $...$ and $$...$$ math via MathJax (loaded via CDN in index.html)
 */
function HtmlWithLatex({ html, style }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) {
      const element = ref.current;

      // −2. Decode Unicode escape sequences (\u2019 → ', \u201c → ", etc.)
      // and HTML entities (&lt;img...&gt;, &nbsp;, &#8217;, etc.) from AI extraction
      const decodeText = (text) => {
        if (!text) return text;
        let t = text;
        if (t.includes("\\u")) {
          t = t.replace(/\\unicode\{x([0-9a-fA-F]+)\}/gi, (_, hex) => String.fromCodePoint(parseInt(hex, 16)));
          t = t.replace(/\\unicodex([0-9a-fA-F]+)/gi, (_, hex) => String.fromCodePoint(parseInt(hex, 16)));
          t = t.replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
          t = t.replace(/\\u\{([0-9a-fA-F]+)\}/g, (_, hex) => String.fromCodePoint(parseInt(hex, 16)));
        }
        if (/&(?:#\d+|#x[0-9a-fA-F]+|[a-zA-Z][a-zA-Z0-9]+);/.test(t)) {
          const textarea = document.createElement("textarea");
          textarea.innerHTML = t;
          t = textarea.value;
          textarea.remove();
        }
        return t;
      };
      const withDecodedUnicode = decodeText(html || "");

      // −1.6. Convert non-breaking spaces (U+00A0 / decoded &nbsp;) to regular spaces.
      // The Quill editor and ~44% of imported questions store EVERY space as &nbsp;,
      // which the browser may not wrap at — forcing ugly mid-word line breaks
      // ("ten" → "te"/"n") on narrow screens. Regular spaces restore word-wrapping.
      const withBreakableSpaces = withDecodedUnicode.replace(/ /g, " ");

      // −1.5. Strip [Formulas: ...] artifact that AI sometimes appends listing all LaTeX symbols.
      const withoutFormulas = withBreakableSpaces.replace(/\[Formulas:[^[\]]*]\s*/gi, "");

      // −1. Unescape markdown \_ → _ (DataLab outputs blank lines as \_\_\_\_\_\_)
      const withUnescapedUnderscores = withoutFormulas.replace(/\\_/g, "_");

      // 0.0. Auto-wrap bare LaTeX commands in $...$ so MathJax renders them.
      const withWrappedLatex = autoWrapLatexCommands(withUnescapedUnderscores);

      // 0.05. Wrap bare caret exponents (5^2, x^3) in $...$ so they render as real powers.
      const withWrappedExponents = wrapBareExponents(withWrappedLatex);

      // 0.1. Fix plain dots inside $...$ math so MathJax can render them
      const withFixedMath = fixDotsInMath(withWrappedExponents);

      // 0.1a. Join PDF/HTML-split hyphenated words — PDFs extract compound adjectives with a
      // line break after the hyphen ("three-\ndigit", "two-<br>digit"). Replace both patterns
      // with U+2011 (non-breaking hyphen) so the word stays on one line.
      const withJoinedHyphens = withFixedMath
        .replace(/(\w)-(?:<br\s*\/?>\s*|\n)(\w)/gi, '$1‑$2');

      // 0.1b. Convert Unicode superscript powers (83² → 83<sup>2</sup>) in both render paths.
      const withSuperscripts = convertUnicodeSuperscripts(withJoinedHyphens);

      // Rich HTML path (Quill editor output): skip markdown transformations that would corrupt
      // already-valid HTML. Only rewrite image URLs, sanitize, and typeset math.
      if (isRichHtml(withSuperscripts)) {
        const fixedHtml = protectCurrencyDollars(withSuperscripts)
          .replace(
            /https?:\/\/[^"'\s>]*\.r2\.[^"'\s>/]+\/([^"'\s>]+)/g,
            (_, path) => getVisualAssetUrl(path)
          )
          .replace(
            /https?:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?\/api\/proxy\/visual\/([^"'\s>]+)/g,
            (_, path) => getVisualAssetUrl(path)
          )
          .replace(
            /https?:\/\/[^/"'\s>]+\/api\/proxy\/visual\/([^"'\s>]+)/g,
            (_, path) => getVisualAssetUrl(path)
          );

        element.innerHTML = DOMPurify.sanitize(fixedHtml, SANITIZE_CONFIG);

        element.querySelectorAll("img").forEach((img) => {
          const raw = img.getAttribute("src");
          if (raw) {
            const resolved = resolveQuestionHtmlAssetUrl(raw);
            if (resolved && resolved !== raw) img.setAttribute("src", resolved);
          }
          img.style.maxWidth = img.style.maxWidth || "100%";
          img.style.height = img.style.height || "auto";
          img.style.display = img.style.display || "block";
          img.style.borderRadius = img.style.borderRadius || "4px";
          img.style.margin = img.style.margin || "8px 0";
          img.onerror = () => { img.style.display = "none"; };
        });

        if (containsMath(fixedHtml)) {
          const _richTypeset = () => {
            if (window.MathJax?.typesetPromise) {
              window.MathJax.typesetPromise([element]).catch(() => {});
              return true;
            }
            return false;
          };
          if (!_richTypeset()) {
            const deadline = Date.now() + 10000;
            const poll = setInterval(() => {
              if (_richTypeset() || Date.now() > deadline) clearInterval(poll);
            }, 200);
          }
        }
        return;
      }

      // Markdown / plain-text path (AI-extracted question text): apply full transformation pipeline.

      // 0. Strip leading question number (e.g. "Q1) ") — QuestionPaperLayout already shows it
      const withoutQNum = stripLeadingQNumber(withSuperscripts);

      // 0.0a. Join PDF-split hyphenated words and replace with U+2011 (non-breaking hyphen).
      // PDFs often store "three-\ndigit" (hyphen at line end, word continues on next line).
      // Also handles same-line "three-digit" to prevent wrap at narrow widths.
      // Skips math delimiters ($...$, \(...\)) to avoid corrupting LaTeX.
      const withNonBreakingHyphens = withoutQNum.replace(
        /(\$[^$]+\$|\\\([^)]*\\\))|(\w)-\n(\w)|(\w)-(\w)/g,
        (m, math, a1, b1, a2, b2) => {
          if (math) return m;
          if (a1 !== undefined) return `${a1}‑${b1}`;
          return `${a2}‑${b2}`;
        }
      );

      // 0.00. Normalize \circ: "307°\circ" → "307°", "45\circ" → "45°"
      const withNormalizedCirc = normalizeCirc(withNonBreakingHyphens);

      // 0.005. Fix odd $ counts and "\frac{a}{b}$" without leading $ (diagram / importer quirks)
      const withBalancedDollars = normalizeDanglingMathDelimiters(withNormalizedCirc);

      // 0.006. Convert display-mode $$...$$ to inline $...$ so fractions/math render inline.
      const withInlineMath = withBalancedDollars.replace(/\$\$([^$]+)\$\$/g, (_m, inner) => `$${inner.trim()}$`);

      // 0.2. Convert \textcircled{N} → styled oval spans
      const withCircled = convertTextCircled(withInlineMath);

      // 0.3. Strip inline MCQ options (A) text B) text...) that AI embeds in question_text
      const withoutMCQ = stripInlineMCQOptions(withCircled);

      // 0.5. Normalize inline tables
      const withNormalizedTables = normalizeInlineTables(withoutMCQ);

      // 0.55. Join split table rows
      const withJoinedRows = joinSplitTableRows(withNormalizedTables);

      // 0.6. Normalize digit rows
      const withDigitRows = normalizeDigitRows(withJoinedRows);

      // 1. Deduplicate repeated question text
      const deduped = dedupeText(withDigitRows);

      // 2. Convert markdown tables to HTML tables
      const withTables = convertMarkdownTables(deduped);

      // 3a. Normalize inline bullet lists
      const withInlineLists = normalizeInlineBulletLists(withTables);

      // 3b. Convert markdown unordered-list lines to <ul><li>
      const withLists = convertMarkdownLists(withInlineLists);

      // 3c. Convert numbered step lines (1. / 2.) to <ol><li>
      const withNumberedLists = convertMarkdownNumberedLists(withLists);

      // 4. Convert remaining \n to <br>
      const withBreaks = convertNewlinesToBr(withNumberedLists);

      // 5. Convert markdown inline formatting: **bold** → <strong>, *italic* → <em>
      const withInline = withBreaks
        .replace(/\*\*([^*<]+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*([^*<]+?)\*/g, '<em>$1</em>');

      // 6. Convert markdown image syntax ![alt](url) → <img>
      const withImages = withInline.replace(
        /!\[([^\]]*)\]\(([^)\s]+)\)/g,
        (_, alt, src) => {
          const resolved = resolveQuestionHtmlAssetUrl(src);
          return `<img src="${resolved}" alt="${alt}" style="display:inline-block;max-width:min(100%,280px);max-height:220px;width:auto;height:auto;border-radius:6px;border:1px solid #e0e0e0;margin:4px 8px 4px 0;vertical-align:top">`;
        }
      );

      // 7. Rewrite R2 and proxy URLs to current-env URL
      const fixedHtml = protectCurrencyDollars(withImages)
        .replace(
          /https?:\/\/[^"'\s>]*\.r2\.[^"'\s>/]+\/([^"'\s>]+)/g,
          (_, path) => getVisualAssetUrl(path)
        )
        .replace(
          /https?:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?\/api\/proxy\/visual\/([^"'\s>]+)/g,
          (_, path) => getVisualAssetUrl(path)
        )
        .replace(
          /https?:\/\/[^/"'\s>]+\/api\/proxy\/visual\/([^"'\s>]+)/g,
          (_, path) => getVisualAssetUrl(path)
        );

      // 8. Sanitize and render
      element.innerHTML = DOMPurify.sanitize(fixedHtml, SANITIZE_CONFIG);

      // 8b. Fix <img src> from raw HTML (e.g. /storage/…, bare question-bank paths) after sanitize
      // 9. Hide broken images (prevents raw alt-text showing for unresolvable URLs)
      element.querySelectorAll("img").forEach((img) => {
        const raw = img.getAttribute("src");
        if (raw) {
          const resolved = resolveQuestionHtmlAssetUrl(raw);
          if (resolved && resolved !== raw) {
            img.setAttribute("src", resolved);
          }
        }
        img.onerror = () => {
          img.style.display = "none";
        };
      });

      // 10. Typeset math with MathJax — wait for async CDN script to fully initialize.
      // Three paths cover all timing scenarios:
      //   A) MathJax already initialized  → startup.promise exists (resolved) → typeset immediately
      //   B) MathJax config loaded, library not yet → listen for 'mathjax:ready' event
      //      (fired by startup.ready() in index.html) AND poll as a fallback
      //   C) window.MathJax not yet defined → poll until it appears
      if (containsMath(fixedHtml)) {
        let _mjPollId = null;
        const _doTypeset = () => {
          if (window.MathJax?.typesetPromise) {
            window.MathJax.typesetPromise([element]).catch((err) =>
              console.error("MathJax rendering error:", err)
            );
            return true;
          }
          return false;
        };
        const _onMjReady = () => {
          if (_mjPollId) { clearInterval(_mjPollId); _mjPollId = null; }
          _doTypeset();
        };

        if (window.MathJax?.startup?.promise) {
          window.MathJax.startup.promise.then(_doTypeset).catch(_doTypeset);
        } else {
          document.addEventListener('mathjax:ready', _onMjReady, { once: true });
          if (!_doTypeset()) {
            // Poll every 200ms for up to 15s as a belt-and-suspenders fallback.
            const deadline = Date.now() + 15000;
            _mjPollId = setInterval(() => {
              if (_doTypeset() || Date.now() > deadline) {
                clearInterval(_mjPollId);
                _mjPollId = null;
                document.removeEventListener('mathjax:ready', _onMjReady);
              }
            }, 200);
          }
        }
      }
    }
  }, [html]);

  return <div ref={ref} style={style} />;
}

export default memo(HtmlWithLatex);
